import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import {
  Eye, Users, TrendingUp, Clock, Sparkles, Smartphone, Globe, Calendar, Loader2
} from 'lucide-react';
import { API } from '../config/api';

const COLORS = ['#C99A55', '#493329', '#8C7261', '#D4B96A', '#AE9885', '#7A6455'];

function KPICard({ icon: Icon, label, value, suffix, accent }) {
  return (
    <Card className={`border-[#E9DDCC] rounded-2xl ${accent ? 'bg-gradient-to-br from-[#F3EADA] to-[#EFE0C4] border-[#C99A55]/40' : ''}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-[#AE9885] text-xs uppercase tracking-wider font-medium mb-2">
          <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
          {label}
        </div>
        <div className="flex items-baseline gap-1">
          <span className={`font-heading text-3xl font-medium ${accent ? 'text-[#C99A55]' : 'text-[#493329]'}`}>
            {value}
          </span>
          {suffix && <span className="text-sm text-[#7A6455]">{suffix}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="font-heading text-lg text-[#493329] mb-4 flex items-center gap-2 mt-2">
      <span className="w-6 h-0.5 bg-[#C99A55] inline-block" />
      {children}
    </h3>
  );
}

export default function CoachStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchStats = async (d = days) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/stats?days=${d}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error('stats error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(days); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#C99A55]" />
      </div>
    );
  }

  const k = data.kpi;

  return (
    <div className="space-y-6" data-testid="coach-stats-tab">
      {/* Range selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-[#7A6455] uppercase tracking-wider mr-1">Période</span>
        {[7, 30, 90].map(d => (
          <Button
            key={d}
            onClick={() => setDays(d)}
            variant="outline"
            size="sm"
            className={`rounded-full text-xs h-8 px-3 ${days === d ? 'bg-[#C99A55] text-white border-[#C99A55] hover:bg-[#493329] hover:text-white' : 'border-[#E9DDCC] text-[#7A6455]'}`}
            data-testid={`stats-range-${d}`}
          >
            {d === 7 ? '7 jours' : d === 30 ? '30 jours' : '90 jours'}
          </Button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard icon={Users} label="Visiteurs uniques" value={k.unique_visitors} />
        <KPICard icon={Eye} label="Pages vues" value={k.page_views} />
        <KPICard icon={Clock} label="Temps moyen" value={k.avg_minutes} suffix="min" />
        <KPICard icon={TrendingUp} label="Conversion" value={k.conversion_rate} suffix="%" />
        <KPICard icon={Sparkles} label="Pré-inscriptions" value={k.registrations} accent />
        <KPICard icon={Sparkles} label="Privilège (-20%)" value={k.registrations_privilege} accent />
        <KPICard icon={Users} label="Standard" value={k.registrations_standard} />
        <KPICard icon={Sparkles} label="Liste VIP totale" value={k.total_privilege_members} />
      </div>

      {/* Timeline */}
      <div>
        <SectionTitle>Visiteurs par jour</SectionTitle>
        <Card className="border-[#E9DDCC] rounded-2xl">
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9DDCC" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7A6455' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#7A6455' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E9DDCC', borderRadius: 12, fontSize: 12 }} />
                <Line type="monotone" dataKey="visitors" stroke="#C99A55" strokeWidth={2.5} dot={false} name="Visiteurs" />
                <Line type="monotone" dataKey="views" stroke="#8C7261" strokeWidth={1.5} dot={false} name="Pages vues" strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top pages */}
        <div>
          <SectionTitle>Pages les plus vues</SectionTitle>
          <Card className="border-[#E9DDCC] rounded-2xl">
            <CardContent className="p-4 space-y-2.5">
              {data.top_pages.length === 0 ? (
                <p className="text-sm text-[#AE9885] italic">Aucune donnée pour le moment.</p>
              ) : data.top_pages.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-[#493329] font-medium truncate max-w-[70%]">{p.path || '/'}</span>
                  <span className="text-[#C99A55] font-semibold">{p.views}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Sources */}
        <div>
          <SectionTitle>Provenance</SectionTitle>
          <Card className="border-[#E9DDCC] rounded-2xl">
            <CardContent className="p-4">
              {data.sources.length === 0 ? (
                <p className="text-sm text-[#AE9885] italic">Aucune donnée pour le moment.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={data.sources} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40}>
                      {data.sources.map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E9DDCC', borderRadius: 12, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {data.sources.map((s, i) => (
                  <div key={i} className="text-[11px] text-[#7A6455] flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    {s.name} <span className="font-semibold text-[#493329]">({s.value})</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Devices */}
        <div>
          <SectionTitle>Appareils</SectionTitle>
          <Card className="border-[#E9DDCC] rounded-2xl">
            <CardContent className="p-4 space-y-3">
              {data.devices.length === 0 ? (
                <p className="text-sm text-[#AE9885] italic">Aucune donnée pour le moment.</p>
              ) : data.devices.map((d, i) => {
                const total = data.devices.reduce((s, x) => s + x.value, 0) || 1;
                const pct = Math.round((d.value / total) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-[#493329] flex items-center gap-1.5"><Smartphone className="w-3 h-3" />{d.name}</span>
                      <span className="text-[#7A6455]">{d.value} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-[#F5F2EA] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Plans */}
        <div>
          <SectionTitle>Formules choisies</SectionTitle>
          <Card className="border-[#E9DDCC] rounded-2xl">
            <CardContent className="p-4">
              {data.plan_breakdown.length === 0 ? (
                <p className="text-sm text-[#AE9885] italic">Aucune pré-inscription sur cette période.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.plan_breakdown} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9DDCC" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7A6455' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#7A6455' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E9DDCC', borderRadius: 12, fontSize: 12 }} />
                    <Bar dataKey="value" fill="#C99A55" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Hourly */}
      <div>
        <SectionTitle>Heures de pointe</SectionTitle>
        <Card className="border-[#E9DDCC] rounded-2xl">
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.hours} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9DDCC" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#7A6455' }} tickFormatter={(h) => `${h}h`} />
                <YAxis tick={{ fontSize: 10, fill: '#7A6455' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E9DDCC', borderRadius: 12, fontSize: 12 }}
                  labelFormatter={(h) => `${h}h00 - ${h}h59`} />
                <Bar dataKey="views" fill="#8C7261" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-[#AE9885] italic text-center pt-2">
        Données sur les {days} derniers jours · Tracking en temps réel · 100% RGPD-friendly (aucune donnée tierce, IP anonymisée)
      </p>
    </div>
  );
}
